import React, { useEffect, useState } from 'react';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { BarChart3, Loader2 } from 'lucide-react';
import { adminAPI } from '../services/api';

const fmtWeek = (iso) => new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });

// Admin › Fournisseurs : weekly « Prévenez-moi » e-mails sent vs orders converted
const AlertsConversionChart = () => {
  const [weeks, setWeeks] = useState(12);
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); adminAPI.stockNotifyStats(weeks).then(setData).catch(() => setData({ weeks: [], totals: {} })); }, [weeks]);
  const t = data?.totals || {};
  const rows = (data?.weeks || []).map(w => ({ ...w, label: fmtWeek(w.start) }));
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-6" data-testid="alerts-conversion-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><BarChart3 size={16} className="text-orange-500" /> Impact des alertes « Prévenez-moi »</h3>
          <p className="text-xs text-gray-500" data-testid="alerts-conversion-totals">{t.sent || 0} e-mail(s) envoyé(s) ({t.sent_stock || 0} retour en stock · {t.sent_price || 0} baisse de prix) · {t.followups || 0} relance(s) · <strong>{t.converted || 0} commande(s)</strong> ({t.rate || 0} %) · {Number(t.revenue || 0).toFixed(2)} € générés</p>
        </div>
        <select value={weeks} onChange={e => setWeeks(Number(e.target.value))} data-testid="alerts-conversion-weeks" className="text-sm border border-gray-200 rounded-md py-1.5 px-2 bg-white">
          {[4, 8, 12, 26, 52].map(n => <option key={n} value={n}>{n} semaines</option>)}
        </select>
      </div>
      {!data ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-orange-500" /></div> : !t.sent && !t.followups ? (
        <p className="text-sm text-gray-500 py-6 text-center" data-testid="alerts-conversion-empty">Aucune alerte envoyée sur la période : le graphique se remplira au fil des retours en stock et baisses de prix.</p>
      ) : (
        <div data-testid="alerts-conversion-chart">
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={rows} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, name) => [v, name]} labelFormatter={(l, p) => p?.[0]?.payload?.week ? `Semaine du ${l} (${p[0].payload.week})` : l} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="sent_stock" stackId="sent" name="Retour en stock" fill="#1e3a5f" radius={[0, 0, 0, 0]} />
              <Bar dataKey="sent_price" stackId="sent" name="Baisse de prix" fill="#7c3aed" radius={[4, 4, 0, 0]} />
              <Bar dataKey="followups" name="Relances J+7" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="converted" name="Commandes converties" stroke="#16a34a" strokeWidth={2.5} dot={{ r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="overflow-x-auto"><table className="w-full text-xs mt-3" data-testid="alerts-conversion-table">
            <thead className="text-gray-500"><tr><th className="text-left py-1 px-2">Semaine</th><th className="text-right py-1 px-2">Envoyés</th><th className="text-right py-1 px-2">Relances</th><th className="text-right py-1 px-2">Commandes</th><th className="text-right py-1 px-2">Taux</th><th className="text-right py-1 px-2">CA</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {rows.filter(w => w.sent_stock + w.sent_price + w.followups > 0).map(w => (
                <tr key={w.week} data-testid={`alerts-conversion-row-${w.week}`}><td className="py-1 px-2">{w.label} <span className="text-gray-400">{w.week}</span></td><td className="text-right py-1 px-2">{w.sent_stock + w.sent_price}</td><td className="text-right py-1 px-2">{w.followups}</td><td className="text-right py-1 px-2 font-semibold text-green-700">{w.converted}</td><td className="text-right py-1 px-2">{w.rate} %</td><td className="text-right py-1 px-2">{Number(w.revenue).toFixed(2)} €</td></tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}
    </div>
  );
};

export default AlertsConversionChart;
